import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Loader } from "@/components/ui-helpers";
import { Home, MessageSquare, Users, CheckSquare, Wallet, Bell, LogOut } from "lucide-react";
import { initials } from "@/mobile/mobileUtils";
import { makeArrivalDetector, playChime } from "@/lib/notifySound";

// Auth guard for the mobile app — staff/admin only, mirrors the web CRM rules.
export function MobileGuard({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loader label="Loading..." />;
  if (user === false) return <Navigate />;
  if (user.role === "client") return <Navigate />;
  return children;
}

function Navigate() {
  const nav = useNavigate();
  useEffect(() => { nav("/m/login", { replace: true }); }, [nav]);
  return null;
}

export function MobileLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let alive = true;
    const detect = makeArrivalDetector();
    const load = () =>
      api.get("/notifications")
        .then((r) => { if (!alive) return; if (detect(r.data || []).length) playChime(); setUnread((r.data || []).filter((n) => !n.read).length); })
        .catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const isAdmin = user?.role === "admin";
  const tabs = [
    { to: "/m", icon: Home, label: "Home", end: true, testid: "mnav-home" },
    { to: "/m/chat", icon: MessageSquare, label: "Chat", testid: "mnav-chat" },
    { to: "/m/clients", icon: Users, label: "Clients", testid: "mnav-clients" },
    { to: "/m/tasks", icon: CheckSquare, label: "Tasks", testid: "mnav-tasks" },
    ...(isAdmin ? [{ to: "/m/expenses", icon: Wallet, label: "Expenses", testid: "mnav-expenses" }] : []),
  ];

  const doLogout = async () => { await logout(); nav("/m/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-background flex flex-col max-w-md mx-auto relative" data-testid="mobile-shell">
      {/* Header */}
      <header className="sticky top-0 z-30 h-14 px-4 flex items-center justify-between bg-secondary text-white shadow-sm" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <Link to="/m" className="flex items-center gap-2" data-testid="mobile-brand">
          <img src="/logo.png" alt="Chahal Overseas" className="h-7 w-auto object-contain" />
          <span className="font-head font-extrabold text-sm leading-none">Chahal <span className="text-[hsl(var(--brand-blue))]">CRM</span></span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/m/notifications" className="relative" data-testid="mobile-bell">
            <Bell className="h-5 w-5" />
            {unread > 0 && <span className="absolute -top-1.5 -right-1.5 bg-[hsl(var(--brand-orange))] text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center" data-testid="mobile-unread-badge">{unread > 9 ? "9+" : unread}</span>}
          </Link>
          <button onClick={doLogout} data-testid="mobile-logout" className="text-white/80 hover:text-white"><LogOut className="h-5 w-5" /></button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto pb-20" data-testid="mobile-content">
        <Outlet />
      </main>

      {/* Bottom tab bar */}
      <nav className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md z-30 bg-white border-t border-slate-200 flex justify-around items-stretch" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="mobile-tabbar">
        {tabs.map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end} data-testid={t.testid}
            className={({ isActive }) => `flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[11px] font-medium transition-colors ${isActive ? "text-primary" : "text-slate-400"}`}>
            {({ isActive }) => (<><t.icon className={`h-5 w-5 ${isActive ? "scale-110" : ""} transition-transform`} /><span>{t.label}</span></>)}
          </NavLink>
        ))}
        <NavLink to="/m/account" data-testid="mnav-account" className={({ isActive }) => `flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[11px] font-medium ${isActive ? "text-primary" : "text-slate-400"}`}>
          <div className="h-5 w-5 rounded-full bg-secondary text-white text-[9px] font-bold flex items-center justify-center">{initials(user?.name)}</div>
          <span>Me</span>
        </NavLink>
      </nav>
    </div>
  );
}
